import type {
  IntrinsicSystemProps,
  MotionPropsFor,
  StackProps,
} from '@hover/blueprint';
import {
  Flex,
  VStack,
  calc,
  forwardRef,
  mapResponsive,
  system,
  useMergeRefs,
} from '@hover/blueprint';
import type { ResponsiveValue } from '@hover/blueprint/chakra';
import { useInView } from 'framer-motion';
import type { ForwardedRef, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router';

import {
  useGenerateImageLoadingState,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Route, generatePath } from 'src/shared/route';
import { imageClicked } from 'src/shared/telemetry';
import { preserveNativeLinking } from 'src/shared/utils/routing';

import { withHover } from '../../helpers/styles';
import type {
  AiOrSampleDisplayImage,
  AiOrSampleOriginalImage,
} from '../../types';
import { isMyUpload, isSampleDisplay } from '../../types';
import type { FullscreenImageThumbnailProps } from '../FullscreenImage';
import { FullscreenImageThumbnail } from '../FullscreenImage';
import {
  MobileThumbnailEngagementButtons,
  selectors as mobileThumbnailEngagementSelectors,
} from '../MobileThumbnailEngagementButtons';
import { ThumbnailEngagementButtons } from '../ThumbnailEngagementButtons';

import { Thumbnail } from './Thumbnail';

interface ImageProps extends MotionPropsFor<IntrinsicSystemProps<'img'>> {
  ref?: ForwardedRef<HTMLImageElement>;
}

const POLLING_CONSUMER_ID = 'generated-image-thumbnail';

export interface GeneratedImageThumbnailProps
  extends StackProps,
    Pick<
      FullscreenImageThumbnailProps,
      'onEnterFullscreen' | 'canEnterFullscreen' | 'enableLayoutAnimation'
    > {
  image: AiOrSampleDisplayImage;
  /**
   * An optional element to display below the image, but above the engagment
   * buttons (on mobile)
   */
  imageAction?: ReactNode;
  imageRef?: ForwardedRef<HTMLImageElement>;
  imageProps?: ImageProps;
  /**
   * By default, clicking on the image opens the image in the fullscreen image
   * view. Set this prop to override the `href` attribute to navigate elsewhere.
   */
  hrefOverride?: string;
  /**
   * Explicit height of the label content displayed below the image. This is
   * required when the size of this component is determined by its height
   * instead of its width.
   */
  labelHeight?: ResponsiveValue<string>;
  orientation?: string | null;
  /**
   * Optionally provide the original image to use as a placeholder while the
   * generated image is generating and loading
   */
  originalImage?: AiOrSampleOriginalImage;
  onViewChange?: (data: { id: number; isInView: boolean }) => void;
}

export const GeneratedImageThumbnail = forwardRef(
  (
    {
      canEnterFullscreen,
      image,
      imageAction,
      imageProps,
      labelHeight,
      onEnterFullscreen,
      orientation: orientationProp,
      enableLayoutAnimation,
      originalImage,
      hrefOverride,
      onViewChange,
      ...props
    }: GeneratedImageThumbnailProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const imageVisibilityRef = useRef<Element>(null);
    const isInView = useInView(imageVisibilityRef, { amount: 0.6 });

    const imageRef = useMergeRefs(imageProps?.ref, imageVisibilityRef);

    const {
      aiGeneratedImage: { style, spaceType },
    } = image;

    const isSampleDisplayImage = isSampleDisplay(image);

    const aiGeneratedImageId = isSampleDisplayImage
      ? undefined
      : image.aiGeneratedImage.id;
    const aiOriginalImageId = isSampleDisplayImage
      ? undefined
      : image?.aiOriginalImageId;
    const sampleOriginalImageId = isSampleDisplayImage
      ? image?.sampleOriginalImageId
      : undefined;

    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
    const { trackingData } = useHomeownerAppContext();

    const history = useHistory();

    const label = isMyUpload(image)
      ? t('originalImageScreen.myUpload')
      : t('generatedImageThumbnail.label', {
          id: aiGeneratedImageId,
          style,
        });

    const { isGeneratedLoading, isLoadingImage, error } =
      useGenerateImageLoadingState({
        consumerId: POLLING_CONSUMER_ID,
        image: image.aiGeneratedImage,
      });

    const href =
      hrefOverride ??
      generatePath(Route.HOMEOWNER_APP_ORIGINAL_DISPLAY_FULLSCREEN.path, {
        aiOriginalImageId: isSampleDisplayImage
          ? image.sampleOriginalImageId
          : image.aiOriginalImageId,
        aiDisplayImageId: isSampleDisplayImage ? image.sampleId : image.id,
      });

    useEffect(() => {
      if (!onViewChange) return;

      onViewChange({ id: image.aiGeneratedImage.id as number, isInView });
    }, [image.aiGeneratedImage.id, isInView, onViewChange]);

    return (
      <VStack
        aria-label={label}
        data-testid="generated-image-thumbnail"
        fontWeight="500"
        position="relative"
        role="listitem"
        spacing="300"
        width="100%"
        {...props}
        ref={ref}
      >
        <system.div
          data-group
          height={
            labelHeight
              ? mapResponsive(
                  height => calc.subtract('100%', height),
                  labelHeight,
                )
              : undefined
          }
          minHeight={0}
          position="relative"
          width="100%"
        >
          <FullscreenImageThumbnail
            _hover={{ filter: 'brightness(0.8)' }}
            aiGeneratedImage={image.aiGeneratedImage}
            as={Thumbnail}
            canEnterFullscreen={canEnterFullscreen}
            consumerId={POLLING_CONSUMER_ID}
            enableLayoutAnimation={enableLayoutAnimation}
            filter="brightness(1)"
            height="100%"
            href={href}
            image={image}
            imageProps={{ ...imageProps, ref: imageRef }}
            label={label}
            onClick={event => {
              if (error) return;

              imageClicked({
                ...trackingData,
                ai_original_image_id: aiOriginalImageId,
                ai_generated_image_id: aiGeneratedImageId,
                output_id: sampleOriginalImageId,
                track_id: 'space-type-full-screen',
                style,
                space_type: spaceType || undefined,
              });

              preserveNativeLinking(event, () => history.push(href));
            }}
            onEnterFullscreen={onEnterFullscreen}
            orientation={orientationProp}
            originalImage={originalImage}
            width="100%"
          />
          {!isGeneratedLoading && !isSampleDisplayImage && !isLoadingImage && (
            <system.div
              bottom={0}
              data-testid="generated-image-thumbnail-overlay"
              left={0}
              pointerEvents="none"
              position="absolute"
              right={0}
              sx={{ containerType: 'inline-size' }}
              top={0}
            >
              <ThumbnailEngagementButtons image={image} />
            </system.div>
          )}
        </system.div>
        {imageAction}
        <Flex
          alignItems="center"
          boxSizing="border-box"
          gap="100"
          height={labelHeight}
          justifyContent="space-between"
          sx={{
            ...withHover({ display: 'none' }),
            containerType: 'inline-size',
          }}
          width="100%"
        >
          {!isGeneratedLoading && !isSampleDisplayImage && !isLoadingImage && (
            <MobileThumbnailEngagementButtons
              flexShrink={0}
              image={image}
              sx={{
                '@container (width < 200px)': {
                  gap: 100,
                  [mobileThumbnailEngagementSelectors.button]: {
                    height: 300,
                    px: 400,
                  },
                  [mobileThumbnailEngagementSelectors.iconButton]: {
                    height: 300,
                    width: 300,
                  },
                },
              }}
            />
          )}
        </Flex>
      </VStack>
    );
  },
);
